// src/encounter/services/use-medication-valueset-search.ts
// Medication value-set lookup carrying route + unit-of-measurement (PROD-1419 / PROD-1434).
//   GET /api/Master/SearchValueSets2?SearchText={q}&SearchFor=Medications
// This mirrors Flutter's `FetchMedicationUseCase` (`valueSetsDataSimpleKey({SearchFor:Medications,
// SearchText:q})`), which is used for a single purpose in the A/P: the dose dialog's by-code route/uom
// fetch (`fetchSelectedMedicationData(med.code)` → `setRouteData`). It is ALSO the catalogue tier of
// the A/P medication search (use-medication-search-tiered.ts: provider `GetMedication` favourites
// first, then this), where `q` is a free-text term; for the dose dialog `q` is the picked med's CODE. The value-set
// row embeds `route[]` + `unitOfMeasurement`, so the dose dialog pre-fills Route + Dose/Dispense unit
// on open. Rows map to MedicationOption (assignable to DS MedicationResult AND MedicationPick).
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MedicationOption, MedRoute } from '../lib/to-medication-result';

/** One route coding on a medication value-set row. */
interface ValueSetRoute {
  code: string;
  display?: string | null;
  system?: string | null;
}
/** One row of SearchValueSets2?SearchFor=Medications — medication + its route(s) + unit. */
interface MedicationValueSetRow {
  valueCoding?: { code?: string | null; display?: string | null; system?: string | null } | null;
  unitOfMeasurement?: string | null;
  route?: (ValueSetRoute | null)[] | null;
}

const MIN_QUERY_LENGTH = 2;

function toRoutes(routes: MedicationValueSetRow['route']): MedRoute[] {
  return (routes ?? [])
    .filter((r): r is ValueSetRoute => !!r && !!r.code)
    .map((r) => ({ code: r.code, display: r.display ?? r.code, ...(r.system ? { system: r.system } : {}) }));
}

function rowToOption(row: MedicationValueSetRow): MedicationOption | null {
  const code = row.valueCoding?.code;
  if (!code) return null; // skip rows without a coding code — nothing to order against
  const routes = toRoutes(row.route);
  return {
    id: code,
    code,
    name: (row.valueCoding?.display ?? code).trim(),
    system: row.valueCoding?.system ?? '',
    ...(routes.length > 0 ? { route: routes } : {}),
    ...(row.unitOfMeasurement ? { unitOfMeasurement: row.unitOfMeasurement } : {}),
  };
}

/**
 * Look up medication value-sets (route + UoM carried) by code (the dose-dialog by-code fetch) or by
 * free-text term. Returns MedicationOption[]; the caller picks the exact-code match or the first row
 * (Flutter takes `r.first`). Retained export name `useMedicationValueSetSearch` for the existing test;
 * `useMedicationValueSet` is the by-code alias the dose dialog imports.
 */
export function useMedicationValueSetSearch(query: string, enabled = true) {
  const q = query.trim();
  return useQuery<MedicationOption[]>({
    queryKey: ['master-medication-valuesets', q],
    enabled: enabled && q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ SearchText: q, SearchFor: 'Medications' });
      const res = await get<MedicationValueSetRow[]>(
        `/api/Master/SearchValueSets2?${qs.toString()}`,
        { signal },
      );
      return (res ?? []).map(rowToOption).filter((o): o is MedicationOption => o !== null);
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

/**
 * By-code route/uom lookup for the dose dialog (Flutter `fetchSelectedMedicationData`). Same endpoint
 * as the search above; named for the by-code call site. `code` is the picked medication's code.
 */
export const useMedicationValueSet = useMedicationValueSetSearch;
